import { useEffect, useLayoutEffect, useRef, useState } from "react";
import CommunicationManager from "../../Helper/CommunicationManager";
import { useChatbarNotificationStore } from "../../Stores/ChatbarNotification";
import { useConfigStore } from "../../Stores/Config";
import { useRouterStore } from "../../Stores/Router";
import { useTicketStore } from "../../Stores/Ticket";
import Conversation from "../Conversation/Conversation";
import ChatbarPill from "./ChatbarPill";
import { AvatarImage } from "../../Components/AvatarImage/AvatarImage";
import { getOperatorInfo } from "../../Components/WidgetHeader/WidgetHeader";
import { ReactComponent as GleapBotIcon } from "../../Assets/GleapBotIcon.svg";
import ChatbarConversationDropdown from "./ChatbarConversationDropdown";
import FormLoading from "../../Components/FormLoading/FormLoading";
import { useSessionStore } from "../../Stores/Session";
import { getActiveChatbarTicket } from "../../Helper/ChatbarActiveTicketCache";
import { calculateContrast } from "../../Helper/ThemeCss";
import "./ChatbarView.scss";

// How long the panel fades out (over the full-size container) before the now-empty
// container collapses to the bar. Keep in sync with chatbar-panel-out in ChatbarView.scss.
const CLOSE_ANIM_MS = 200;

export default function ChatbarView() {
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing] = useState(false);
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [creating, setCreating] = useState(false);
  // Set once the user dismisses the conversation starters via the X; keeps them hidden
  // for the rest of the session.
  const [quickActionsDismissed, setQuickActionsDismissed] = useState(false);
  const ticketStore = useTicketStore();
  const configStore = useConfigStore();
  const config = configStore.config;

  const placeholder = config?.chatbarPlaceholder || "Ask me anything…";
  // The server delivers quick actions as plain localized strings (capped at 2); the SDK
  // may pass { label, text } objects. Normalize both to { label, text } — a bare string
  // is its own label AND the text we send. (The old code read qa.label/qa.text off a
  // string → undefined, which is why the pills rendered empty and clicks did nothing.)
  const quickActions = (config?.chatbarQuickActions ?? [])
    .map((qa) =>
      typeof qa === "string"
        ? { label: qa, text: qa }
        : {
            label: qa?.label ?? qa?.text ?? "",
            text: qa?.text ?? qa?.label ?? "",
          },
    )
    .filter((qa) => qa.text.trim().length > 0)
    .slice(0, 2);

  // Map the chatbar style to the two visual modifiers used in the SCSS (mirrors the
  // canonical BannerApp logic): dark surface + animated glow ring.
  const styleVariant = config?.chatbarStyle || "light-glow";
  const isDark = styleVariant === "dark" || styleVariant === "dark-glow";
  const isGlow = styleVariant !== "light" && styleVariant !== "dark";
  // A light chatbar on a dark widget theme (dark background color): the chrome is
  // hard-coded light, so pin the conversation palette to dark-on-light too — the
  // :root theme vars would otherwise render white text on the light panel.
  const isDarkTheme =
    calculateContrast(configStore.getBackgroundColor()) === "#ffffff";
  const isLightOnDarkTheme = !isDark && isDarkTheme;

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listLoadedRef = useRef(false);
  // Set when the user starts a "New chat" so the next focus doesn't snap back to
  // the cached conversation. Reset on minimize so a later session reopens again.
  const suppressReopenRef = useRef(false);
  // Focusing the collapsed bar opens the panel, which remounts the input pill under
  // the expanded tree (a different DOM node) — so the initial focus is lost and the
  // user has to click twice. Armed only on the collapsed→expanded focus transition;
  // a layout effect restores focus to the newly mounted input before paint.
  const refocusOnExpandRef = useRef(false);

  const openPanel = () => {
    setClosing(false);
    setExpanded(true);
    // Lazy-load the conversation list the first time the panel opens — never on
    // boot if the user never opens it.
    if (!listLoadedRef.current) {
      listLoadedRef.current = true;
      ticketStore.unlockChatbarList();
      ticketStore.loadConversations();
    }
  };

  // Play the exit animation, then unmount + shrink the container.
  const closePanel = () => {
    if (closing) return;
    setClosing(true);
    window.setTimeout(() => {
      setExpanded(false);
      setClosing(false);
      // Fresh session: allow focus to reopen the cached conversation again.
      suppressReopenRef.current = false;
      // Cancel any in-flight creation spinner (e.g. minimized mid-create).
      setCreating(false);
    }, CLOSE_ANIM_MS);
  };

  // The chatbar iframe is same-origin (about:blank bootstrap), so we can read the HOST
  // page's viewport to cap the expanded panel. window.innerHeight here would be the
  // iframe's own (small) height, not the browser viewport — hence window.parent.
  const expandedHeight = (() => {
    try {
      const vh =
        (window.parent && window.parent.innerHeight) || window.innerHeight;
      return Math.min(640, Math.round(vh * 0.8));
    } catch (e) {
      return Math.min(640, Math.round(window.innerHeight * 0.8));
    }
  })();

  // Collapsed bar grows when focused or while typing (like the old chatbar). The SDK
  // container has `transition: width/height`, so changing the posted size animates.
  const collapsedWidth = focused || value.trim().length > 0 ? 480 : 340;

  // Tell the host SDK how tall/wide to make the chatbar iframe container.
  useEffect(() => {
    const post = (width: number, height: number) =>
      CommunicationManager.getInstance().sendMessage({
        name: "chatbar-resize",
        data: { width, height },
      });

    // Whenever expanded (INCLUDING the close fade), keep the container at full height +
    // 480 width. The panel fades over the full-size container so its content is never
    // clipped ("stuck"). Only once the panel has faded out and unmounted (expanded =
    // false) does the now-empty container collapse to the measured bar height.
    if (expanded) {
      post(480, expandedHeight);
      return;
    }

    // Collapsed: dynamic width + measured content height.
    const el = rootRef.current;
    if (!el) return;
    const report = () =>
      post(collapsedWidth, Math.ceil(el.getBoundingClientRect().height));
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, expandedHeight, collapsedWidth]);

  // Frost ONLY the panel: tell the SDK the panel's insets so it can place a panel-shaped
  // blur backdrop behind the transparent iframe (not behind the padding, gap, or bar).
  // Insets match .chatbar-expanded: 16 top / 18 sides, and bottom = bottom padding (22) +
  // input bar (~44) + gap (10) = 76. Fades out at the start of close (with the panel).
  useEffect(() => {
    const send = (data: any) =>
      CommunicationManager.getInstance().sendMessage({
        name: "chatbar-panel-blur",
        data,
      });
    if (expanded && !closing) {
      send({
        visible: true,
        top: 16,
        left: 18,
        right: 18,
        bottom: 76,
        radius: 20,
        dark: isDark,
      });
    } else {
      send({ visible: false });
    }
  }, [expanded, closing, isDark]);

  // Collapse the panel when the host SDK reports a click outside the chatbar. The
  // SDK only listens for outside clicks (it never blocks them) and bumps this
  // counter via CommunicationManager; we react here rather than adding a second
  // window message listener.
  const closeRequestId = useChatbarNotificationStore((s) => s.closeRequestId);
  // A live notification pill takes the space above the bar; suppress the conversation
  // starters while one is shown so they don't stack on top of the notification.
  const notificationPill = useChatbarNotificationStore((s) => s.pill);
  const lastCloseRef = useRef(closeRequestId);
  useEffect(() => {
    if (closeRequestId === lastCloseRef.current) return; // initial mount / no change
    lastCloseRef.current = closeRequestId;
    if (expanded) closePanel();
  }, [closeRequestId, expanded]);

  // Gleap.startAgent() on the host SDK bumps this counter. Open the panel and
  // start a fresh conversation with the requested agent — mirrors handleNewChat,
  // but the workflow/bot is the agent the SDK asked for (not the configured
  // default) and any initial message is delivered as the first question.
  const startAgentRequestId = useChatbarNotificationStore(
    (s) => s.startAgentRequestId,
  );
  const lastStartAgentRef = useRef(startAgentRequestId);
  useEffect(() => {
    if (startAgentRequestId === lastStartAgentRef.current) return; // initial mount
    lastStartAgentRef.current = startAgentRequestId;
    const params = useChatbarNotificationStore.getState().startAgentParams;
    if (!params) return;

    // A real agentId → start a DIRECT agent conversation: the server engages
    // the agent without a workflow (see createSharedTicket), sent as `agentId`.
    // 'kai'/default → the configured chatbar workflow, sent as `botId`.
    const isDirectAgent = !!params.agentId && params.agentId !== "kai";
    const botId = isDirectAgent
      ? ""
      : useConfigStore.getState().config?.chatbarWorkflowId || "";
    // initialQuestion → delivered as the customer's first message + answered.
    // initialMessage  → a greeting shown first FROM the agent (static).
    const question = params.initialQuestion?.trim();
    const greeting = params.initialMessage?.trim();

    (async () => {
      suppressReopenRef.current = true;
      setListOpen(false);
      setValue("");
      useChatbarNotificationStore.getState().clear();
      useTicketStore.getState().clearCurrentTicket();
      setCreating(true);
      openPanel();
      await useTicketStore.getState().createNewConversation(
        {
          isManualStart: true,
          aiQuestion: !!question,
          ...(isDirectAgent ? { agentId: params.agentId } : { botId }),
          ...(greeting ? { initialMessage: greeting } : {}),
          context: params.context,
        },
        question || undefined,
      );
      const shareToken = useRouterStore.getState().currentData?.shareToken;
      if (shareToken) {
        useTicketStore.getState().loadCurrentTicket(shareToken);
      } else {
        setCreating(false); // nothing to load → stop the spinner
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAgentRequestId]);

  // Restore focus to the input after a focus-initiated open remounts it under the
  // expanded tree (see refocusOnExpandRef). useLayoutEffect runs before paint, so
  // there's no visible blur and the user never has to click twice.
  useLayoutEffect(() => {
    if (expanded && refocusOnExpandRef.current) {
      refocusOnExpandRef.current = false;
      inputRef.current?.focus();
    }
  }, [expanded]);

  // The creation spinner shows while a new conversation is being created + loaded.
  // Clear it as soon as the conversation appears — currentTicket takes render
  // priority over the spinner, so there's no blank flash in between.
  useEffect(() => {
    if (creating && ticketStore.currentTicket) setCreating(false);
  }, [creating, ticketStore.currentTicket]);

  // Focus is the only trigger that reopens the latest conversation. Nothing loads
  // into currentTicket on boot — the reopen is driven by the locally cached ticket
  // id (gleap-chatbar-active-ticket:<gleapId>), so it's instant and doesn't wait
  // on the conversation list. Reconciliation against the fresh fetch (DONE/CLOSED
  // → collapse) lives in the store's reopenCachedChatbarTicket action.
  const handleFocusReopen = () => {
    setFocused(true);

    // A reopen is already in flight → don't kick off a second fetch.
    if (reopening) return;

    // Already showing a conversation (re-focus without minimizing) → just pop it.
    if (ticketStore.currentTicket) {
      // Arm only on a real collapsed→expanded transition; if already expanded the
      // input isn't remounted, and arming would let a re-entrant focus steal it.
      if (!expanded) refocusOnExpandRef.current = true;
      openPanel();
      return;
    }

    // User just started a New chat → keep the blank bar; don't snap back.
    if (suppressReopenRef.current) return;

    const cached = getActiveChatbarTicket(
      useSessionStore.getState().session?.gleapId,
    );
    if (!cached) return; // nothing to reopen → blank bar just widens

    if (!expanded) refocusOnExpandRef.current = true;
    setReopening(true);
    openPanel(); // optimistic: panel opens now, shows the loading indicator
    ticketStore
      .reopenCachedChatbarTicket()
      .then((opened) => {
        // Resolved / gone → fall back to the blank bar (cache already cleared).
        if (!opened) closePanel();
      })
      .finally(() => setReopening(false));
  };

  // Mirror Conversation.getComposerType so our single input pill routes a message the
  // same way the widget composer would.
  const getComposerType = (): "textInput" | "botTextInput" | "botInput" => {
    const ticket = ticketStore.currentTicket as any;
    if (!ticket?.bot?.active) return "textInput";
    const t = ticketStore.currentBotAction?.action?.type;
    const free =
      t === "answerbotflow" ||
      t === "aitask" ||
      t === "chatinput" ||
      t === "passtoagent";
    if (free || !ticketStore.currentBotAction?.action) return "botTextInput";
    return "botInput";
  };

  // Start a brand-new conversation from the collapsed bar.
  const startNew = async (question: string) => {
    const text = question.trim();
    if (!text) return;
    setValue("");
    useChatbarNotificationStore.getState().clear();
    setCreating(true);
    openPanel();
    await ticketStore.createNewConversation(
      {
        isManualStart: true,
        aiQuestion: true,
        botId: configStore.config?.chatbarWorkflowId || "",
      },
      text,
    );
    const shareToken = useRouterStore.getState().currentData?.shareToken;
    if (shareToken) {
      ticketStore.loadCurrentTicket(shareToken);
    } else {
      setCreating(false); // nothing to load → stop the spinner
    }
  };

  // Send into the already-open conversation, routing like the widget composer.
  const sendToCurrent = (text: string) => {
    const t = text.trim();
    if (!t) return;
    // Closed for the user → nothing may be sent (see conversationClosed below).
    if (ticketStore.currentTicket?.conversationClosed) return;
    setValue("");
    if (getComposerType() === "botTextInput") {
      if (ticketStore.currentBotAction?.action?.type === "chatinput") {
        ticketStore.answerBotAction({ text: t, message: t }, []);
      } else {
        ticketStore.answerBotAction(
          {
            question: t,
            message: t,
            clientTools: useConfigStore.getState().agentTools,
            isFirstMessage: !ticketStore.hasAnswerBotReply(),
          },
          [],
        );
      }
    } else {
      ticketStore.sendComment(t, []);
    }
  };

  // From the collapsed bar: continue the reopened conversation if there is one,
  // otherwise start a new one. Either way the panel opens.
  const sendFromBar = (text: string) => {
    if (!text.trim()) return;
    if (ticketStore.currentTicket) {
      openPanel();
      sendToCurrent(text);
    } else {
      startNew(text);
    }
  };

  // Resolve the header avatar + name from the open conversation (bot or assigned
  // human), falling back to the bot when the panel is blank.
  const headerOperator = (ticketStore.currentTicket
    ? getOperatorInfo(configStore, config, ticketStore.currentTicket)
    : null) ?? {
    image: (
      <AvatarImage
        src={config?.operatorAvatarImage}
        alt={configStore.getBotName()}
        fallback={<GleapBotIcon />}
      />
    ),
    name: configStore.getBotName(),
    bot: true,
  };

  const handleSelectConversation = (shareToken: string) => {
    setListOpen(false);
    ticketStore.loadCurrentTicket(shareToken);
  };

  const handleNewChat = async () => {
    // Create the conversation immediately (like the widget's send button:
    // createNewConversation({ isManualStart: true })) so its workflow / bot
    // greeting runs BEFORE the user writes anything. The first user message then
    // routes into this existing ticket via sendFromBar → sendToCurrent.
    suppressReopenRef.current = true;
    setListOpen(false);
    setValue("");
    useChatbarNotificationStore.getState().clear();
    ticketStore.clearCurrentTicket();
    setCreating(true);
    openPanel();
    await ticketStore.createNewConversation({
      isManualStart: true,
      botId: configStore.config?.chatbarWorkflowId || "",
    });
    const shareToken = useRouterStore.getState().currentData?.shareToken;
    if (shareToken) {
      ticketStore.loadCurrentTicket(shareToken);
    } else {
      setCreating(false); // nothing to load → stop the spinner
    }
    inputRef.current?.focus();
  };

  // A conversation closed "for the user" (ticket.conversationClosed, set by the
  // close-ticket workflow step) must not accept replies. The panel's Conversation
  // view already hides its composer for this case; the persistent bar pill lives
  // outside <Conversation /> and has to apply the same gate itself — otherwise the
  // customer can type into a closed conversation and only learns about it from the
  // server's 409.
  const conversationClosed = !!ticketStore.currentTicket?.conversationClosed;

  // While a structured bot action (rating / data collection) is active, the panel's
  // BotActionInput owns the input — hide the free-text pill, like the widget does.
  const showComposer = !conversationClosed && getComposerType() !== "botInput";
  const composerPlaceholder = conversationClosed
    ? configStore.translateText("conversationEnded")
    : placeholder;

  // The single, persistent input pill — identical markup in both states. The two
  // .chatbar-input-border-* layers sit behind the bar and draw the animated glow ring
  // (hidden in the no-glow variants via .chatbar--noglow). The send button's active /
  // dark colors are driven by the root variant class, not by the button itself.
  const renderComposer = (onSend: (t: string) => void) => {
    const hasText = value.trim().length > 0;
    return (
      <div className={`chatbar-input-container ${!showComposer ? "chatbar-input-container--disabled" : ""}`}>
        <div className="chatbar-input-border-wrapper" />
        <div className="chatbar-input-border-glow" />
        <form
          className="chatbar-input-bg"
          onSubmit={(e) => {
            if (!showComposer) return;
            e.preventDefault();
            onSend(value);
          }}
        >
          <input
            ref={inputRef}
            className={`chatbar-input-field ${showComposer ? "" : "chatbar-input-field--disabled"}`}
            aria-label={composerPlaceholder}
            placeholder={composerPlaceholder}
            autoComplete="off"
            value={value}
            disabled={!showComposer}
            onInput={(e) => {
              if (showComposer) {
                setValue((e.target as HTMLInputElement).value);
              }
            }}
            onFocus={() => {
              if (showComposer) {
                handleFocusReopen();
              }
            }}
            onBlur={() => setFocused(false)}
          />
          <button
            type="submit"
            className="chatbar-send-btn"
            disabled={!hasText}
            aria-label="Send"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        </form>
      </div>
    );
  };

  // Visual variant modifiers shared by the collapsed + expanded roots. `--active`
  // (text present) flips the send button to its filled state.
  const rootVariantClass = [
    isDark ? "chatbar--dark" : "",
    isLightOnDarkTheme ? "chatbar--light-palette" : "",
    !isGlow ? "chatbar--noglow" : "",
    value.trim().length > 0 ? "chatbar--active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (!expanded) {
    return (
      <div className={`chatbar-collapsed ${rootVariantClass}`} ref={rootRef}>
        <ChatbarPill
          onOpen={(shareToken?: string) => {
            useChatbarNotificationStore.getState().clear();
            // Tell the host SDK the pill was read so sibling tabs dismiss theirs
            // too (see the SDK's GleapAiChatbarManager chatbar-notification-read
            // handler). Only fired on a genuine read — starting a new chat clears
            // the pill locally without dismissing it in other tabs.
            CommunicationManager.getInstance().sendMessage({
              name: "chatbar-notification-read",
            });
            if (shareToken) ticketStore.loadCurrentTicket(shareToken);
            openPanel();
          }}
        />
        {/* Conversation starters, stacked above the bar (Intercom-style). Hidden while
            a notification pill is showing, or once the user dismisses them via the X. */}
        {!ticketStore.currentTicket &&
          !notificationPill &&
          !quickActionsDismissed &&
          quickActions.length > 0 && (
            <div className="chatbar-quick-actions">
              {quickActions.map((qa, i) => (
                <button
                  key={i}
                  className="chatbar-quick-action"
                  onClick={() => sendFromBar(qa.text)}
                >
                  {qa.label}
                </button>
              ))}
              <button
                className="chatbar-quick-actions-dismiss"
                onClick={() => setQuickActionsDismissed(true)}
                aria-label="Dismiss suggestions"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
        {renderComposer(sendFromBar)}
      </div>
    );
  }

  return (
    <div
      className={`chatbar-expanded ${rootVariantClass}`}
      style={{ height: expandedHeight }}
    >
      <div
        className={`chatbar-panel ${closing ? "chatbar-panel--closing" : ""}`}
      >
        <div className="chatbar-header">
          <button
            className="chatbar-header-operator"
            onClick={() => setListOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={listOpen}
          >
            <span className="chatbar-header-avatar">
              {headerOperator.image}
            </span>
            <span className="chatbar-header-title">{headerOperator.name}</span>
            <svg
              className={`chatbar-header-caret ${
                listOpen ? "chatbar-header-caret--open" : ""
              }`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 640 640"
            >
              <path
                fill="currentColor"
                d="M303.5 473C312.9 482.4 328.1 482.4 337.4 473L537.4 273C546.8 263.6 546.8 248.4 537.4 239.1C528 229.8 512.8 229.7 503.5 239.1L320.5 422.1L137.5 239.1C128.1 229.7 112.9 229.7 103.6 239.1C94.3 248.5 94.2 263.7 103.6 273L303.6 473z"
              />
            </svg>
          </button>
          <button
            className="chatbar-collapse"
            onClick={closePanel}
            aria-label="Minimize"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
              <path
                fill="#000000"
                d="M303.5 473C312.9 482.4 328.1 482.4 337.4 473L537.4 273C546.8 263.6 546.8 248.4 537.4 239.1C528 229.8 512.8 229.7 503.5 239.1L320.5 422.1L137.5 239.1C128.1 229.7 112.9 229.7 103.6 239.1C94.3 248.5 94.2 263.7 103.6 273L303.6 473z"
              />
            </svg>
          </button>
        </div>
        <div className="chatbar-body-wrap">
          {listOpen && (
            <ChatbarConversationDropdown
              tickets={ticketStore.previousTickets as any}
              loading={!ticketStore.previousTicketsLoaded}
              currentShareToken={ticketStore.currentTicket?.shareToken}
              onSelect={handleSelectConversation}
              onNewChat={handleNewChat}
              onClose={() => setListOpen(false)}
            />
          )}
          <div className="chatbar-body">
            {/* Open straight into the (blank or reopened) chat — never the conversation list. */}
            {ticketStore.currentTicket ? (
              <Conversation />
            ) : creating || reopening ? (
              <div className="chatbar-loading">
                <FormLoading />
              </div>
            ) : (
              <div className="chatbar-empty" />
            )}
          </div>
        </div>
      </div>
      {renderComposer(sendFromBar)}
    </div>
  );
}
